import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import MemberCard from '../../atoms/Dashboard/Members/MemberCard'
import { useAppDispatch, useAppSelector } from '../../../Redux/Hooks'
import { useNavigate } from 'react-router-dom'
import { getAllMembers } from '../../../Redux/ApiCalls/Dashboard/MembersAPI'
import { Member } from '../../../Data/member.dto'
import { clearError, clearFlagsMembers } from '../../../Redux/Slice/Dashboard/MemberSlice'
import { device } from '../../atoms/Utlis/media'
import MarketplaceEmptyState, { MarketplaceLoadingSpinner } from './Marketplace/MarketplaceEmptyState'
import { Plus, Users, Sparkles, UserPlus, Shield } from 'lucide-react'
import PermissionGate from '../../atoms/Utlis/PermissionGate'

const AllMembersPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { members, loading, isError, error, isMemberFetched } = useAppSelector(state => state.member)
  const { selectedBranch } = useAppSelector(state => state.auth)
  const [allMembers, setAllMembers] = useState<Member[] | []>([])

  useEffect(() => {
    dispatch(getAllMembers(selectedBranch?.id))
  }, [dispatch, selectedBranch])

  useEffect(() => {
    setAllMembers(members)
  }, [members])

  useEffect(() => {
    return () => {
      dispatch(clearError())
      dispatch(clearFlagsMembers())
    }
  }, [dispatch])

  const memberCount = (allMembers ?? []).length

  return (
    <PageContainer>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroText>
            <div className="flex flex-col">
              <HeroBadge>
                <Sparkles size={13} />
                Team management
              </HeroBadge>
              <PageTitle className="linear-text">Members</PageTitle>
              <PageSubtitle>
                Manage your team members, roles, and studio access.
              </PageSubtitle>
            </div>
          </HeroText>
          <div className="flex items-center gap-3">
            <PermissionGate permission="role_management.read">
              <RolesHeaderButton type="button" onClick={() => navigate('/dashboard/roles')}>
                <Shield size={16} />
                Roles & Access
              </RolesHeaderButton>
            </PermissionGate>
            <PermissionGate permission="members.create">
              <AddButton type="button" onClick={() => navigate('/dashboard/members/create/new')}>
                <Plus size={18} strokeWidth={2.4} />
                Add member
              </AddButton>
            </PermissionGate>
          </div>
        </HeroContent>
      </Hero>

      {!loading && !isError && (
        <StatsGrid>
          <WalletHero>
            <WalletIconWrap>
              <Users size={16} />
            </WalletIconWrap>
            <WalletHeroBody>
              <WalletHeroValue>{memberCount}</WalletHeroValue>
              <WalletHeroLabel>Total members</WalletHeroLabel>
            </WalletHeroBody>
          </WalletHero>
        </StatsGrid>
      )}

      <Panel>
        {loading ? (
          <MarketplaceLoadingSpinner padding="60px 22px" />
        ) : isError ? (
          <ErrorState>
            <ErrorTitle>Couldn&apos;t load members</ErrorTitle>
            <ErrorHint>{error ? String(error) : 'Please try again.'}</ErrorHint>
          </ErrorState>
        ) : allMembers && allMembers.length > 0 ? (
          <MembersGrid>
            <PermissionGate permission="members.create">
              <AddMemberCard onClick={() => navigate('/dashboard/members/create/new')}>
                <AddMemberIconWrap>
                  <UserPlus size={32} color="#c94daa" strokeWidth={1.8} />
                </AddMemberIconWrap>
                <AddMemberText>Add Member</AddMemberText>
              </AddMemberCard>
            </PermissionGate>
            {allMembers.map((member) => (
              <MemberCard key={member.id} member={member} />
            ))}
          </MembersGrid>
        ) : (
          <MarketplaceEmptyState
            padding="48px 22px"
            icon={<Users size={36} strokeWidth={1.5} />}
            title="No members yet"
            description="Add your first team member to manage access, roles, and collaboration across your studio."
            actions={[
              {
                label: 'Add Your First Member',
                onClick: () => navigate('/dashboard/members/create/new'),
                icon: <Plus size={15} />,
              },
            ]}
            hint={{
              icon: <Sparkles size={13} />,
              text: 'Invite photographers, editors & studio staff',
            }}
          />
        )}
      </Panel>
    </PageContainer>
  )
}

export default AllMembersPage

const gradient = 'linear-gradient(144.01deg, rgba(255, 201, 81, 0.8) 0%, rgba(206, 86, 176, 0.8) 100%)'

const PageContainer = styled.div`
  width: 100%;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex-shrink: 0;
  box-sizing: border-box;
  @media ${device.tablet} { padding: 16px; }
`

const Hero = styled.div`
  position: relative;
  overflow: visible;
  flex-shrink: 0;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -4%;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.32) 0%, transparent 70%);
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`

const HeroText = styled.div`
  min-width: 0;
`

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  margin-bottom: 4px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(201, 77, 170, 0.14);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;
`

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  line-height: 1.25;
  padding: 2px 0;
`

const PageSubtitle = styled.p`
  margin: 0px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 560px;
`

const RolesHeaderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #ffffff;
  color: #171717;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #fdf2f8;
    border-color: #c94daa;
    color: #c94daa;
  }
`

const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  white-space: nowrap;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(201, 77, 170, 0.28);
  }
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 280px);
  gap: 12px;
  flex-shrink: 0;
`

const WalletHero = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  color: #fff;
  background: ${gradient};
  box-shadow: 0 8px 22px rgba(201, 77, 170, 0.22);
  text-align: left;
  &::after {
    content: "";
    position: absolute;
    right: -20px;
    bottom: -24px;
    width: 90px;
    height: 90px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
  }
`

const WalletIconWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.22);
`

const WalletHeroBody = styled.div`
  position: relative;
  z-index: 1;
  min-width: 0;
`

const WalletHeroValue = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
`

const WalletHeroLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  margin-top: 2px;
`

const Panel = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  border-radius: 20px;
  box-shadow: 0 10px 32px rgba(23, 23, 23, 0.06);
  overflow: hidden;
  flex-shrink: 0;
`

const MembersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 24px;
  padding: 22px;
  overflow-y: auto;
  align-content: start;
  @media ${device.laptop} { gap: 20px; padding: 16px; }
`

const AddMemberCard = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #fff;
  border: 2px dashed rgba(201, 77, 170, 0.25);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(201, 77, 170, 0.12);
    border-color: rgba(201, 77, 170, 0.45);
  }
`

const AddMemberIconWrap = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(201, 77, 170, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
`

const AddMemberText = styled.p`
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #171717;
  margin: 0;
`

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 240px;
  padding: 22px;
`

const ErrorTitle = styled.p`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #171717;
`

const ErrorHint = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.6);
  text-align: center;
`
